import React, { useCallback, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  addCrossClientLandingFinishedExecutions,
  selectCrossClientLandingFinishedExecutionProgresses,
  selectCrossClientLandingFinishedExecutions,
  selectCrossClientLandingFinishedExecutionsLoadingMore,
  selectCrossClientLandingRunningExecutionProgresses,
  selectCrossClientLandingRunningExecutions,
  selectCrossClientLandingTotalFinishedExecutions,
  setCrossClientLandingFinishedExecutionsLoadingMore,
  setCrossClientLandingTotalFinishedExecutions,
  stopCrossClientLandingExecution,
} from '../../../../store/crossClient/crossClientLanding'
import { CrossClientQueryExecution, CrossClientQueryPhases } from '../../../../models/CrossClient'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useLazyGetCrossClientQueryExecutionsQuery } from '../../../../store/crossClient/crossClient'
import { Button } from '../../../../components/Button/Button'
import { CrossClientRunningExecutionsPoller } from '../RunningExecutions/CrossClientRunningExecutionsPoller'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { useCancelCrossClientQueryMutation } from '../../../../store/crossClient/crossClientExecution'
import { CrossClientStopModal } from '../../Modal/CrossClientStopModal'
import './CrossClientLandingHistory.scss'
import { CrossClientHistoryElement } from './CrossClientHistoryElement'

const SHOW_MORE_PAGE_SIZE = 5

export interface CrossClientLandingHistoryProps {
  rerunDisabled: boolean
  onRerun: (execution: CrossClientQueryExecution) => void
}

export const CrossClientLandingHistory: React.FC<CrossClientLandingHistoryProps> = ({ rerunDisabled, onRerun }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const finishedExecutions = useAppSelector(selectCrossClientLandingFinishedExecutions)
  const finishedExecutionProgresses = useAppSelector(selectCrossClientLandingFinishedExecutionProgresses)
  const runningExecutions = useAppSelector(selectCrossClientLandingRunningExecutions)
  const runningExecutionProgresses = useAppSelector(selectCrossClientLandingRunningExecutionProgresses)
  const finishedExecutionsLoadingMore = useAppSelector(selectCrossClientLandingFinishedExecutionsLoadingMore)
  const totalFinishedExecutions = useAppSelector(selectCrossClientLandingTotalFinishedExecutions)

  const [triggerGetCrossClientExecutions] = useLazyGetCrossClientQueryExecutionsQuery()
  const [triggerCancelCrossClientQuery, { isLoading: cancelLoading }] = useCancelCrossClientQueryMutation()

  const [stoppingExecution, setStoppingExecution] = useState<CrossClientQueryExecution | null>(null)

  const getFinishedExecutions = useCallback(
    async (page: number) => {
      try {
        dispatch(setCrossClientLandingFinishedExecutionsLoadingMore(true))
        const executions = await triggerGetCrossClientExecutions({
          accountId,
          phases: [CrossClientQueryPhases.COMPLETED, CrossClientQueryPhases.CANCELED],
          pageRequest: { page, size: SHOW_MORE_PAGE_SIZE },
        }).unwrap()
        if (!executions.data.length) {
          return
        }
        dispatch(addCrossClientLandingFinishedExecutions(executions.data))
        dispatch(setCrossClientLandingTotalFinishedExecutions(executions.page.totalElements))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientLandingFinishedExecutionsLoadingMore(false))
      }
    },
    [dispatch, accountId, triggerGetCrossClientExecutions],
  )

  const handleShowMore = async () => {
    if (finishedExecutions.length >= totalFinishedExecutions) {
      return
    }
    const page = Math.floor(finishedExecutions.length / SHOW_MORE_PAGE_SIZE)
    await getFinishedExecutions(page)
  }

  const handleClickStopExecution = (execution: CrossClientQueryExecution) => {
    setStoppingExecution(execution)
  }

  const handleStopExecution = tracked(
    async () => {
      if (!stoppingExecution) {
        return
      }
      try {
        await triggerCancelCrossClientQuery({ accountId, executionId: stoppingExecution.id }).unwrap()
        dispatch(stopCrossClientLandingExecution(stoppingExecution))
      } catch (e) {
        console.error(e)
      }
      setStoppingExecution(null)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_STOPPED,
      eventDetails: { executionId: stoppingExecution?.id },
    },
    { condition: !!stoppingExecution },
  )

  const handleContinueExecution = () => {
    setStoppingExecution(null)
  }

  return (
    <>
      {stoppingExecution ? (
        <CrossClientStopModal show={!!stoppingExecution} loading={cancelLoading} onStop={handleStopExecution} onContinue={handleContinueExecution} />
      ) : null}
      <CrossClientRunningExecutionsPoller />
      <div className='d-flex flex-column gap-3 align-items-center'>
        {runningExecutions.map((execution) => {
          const progress = runningExecutionProgresses.find((p) => p.executionId === execution.id)
          return (
            <CrossClientHistoryElement
              key={execution.id}
              execution={execution}
              progress={progress}
              rerunDisabled={rerunDisabled}
              onStop={handleClickStopExecution}
              onRerun={onRerun}
            />
          )
        })}
        {finishedExecutions.map((execution) => {
          const progress = finishedExecutionProgresses.find((p) => p.executionId === execution.id)
          return (
            <CrossClientHistoryElement
              key={execution.id}
              execution={execution}
              progress={progress}
              rerunDisabled={rerunDisabled}
              onStop={handleClickStopExecution}
              onRerun={onRerun}
            />
          )
        })}
        {finishedExecutions.length < totalFinishedExecutions ? (
          <Button variant='header' className='button-small zocks-border-0.5' onClick={handleShowMore} loading={finishedExecutionsLoadingMore}>
            Show more…
          </Button>
        ) : null}
      </div>
    </>
  )
}
